import { EventEmitter } from 'eventemitter3';
import {
  Map,
  MapOptions,
  PointLike,
  LngLat,
  LngLatLike,
  LngLatBounds,
  EasingOptions,
  StyleSpecification,
} from 'mapbox-gl';
import { FeatureStateDelegate, LayerDelegate } from '..';
import { FilterKing } from '../filter';
import LMFeature from '../map-geometry/LMFeature';
import LivingMapPlugin from '../plugins/base-plugin';
import PluginHelper from '../plugins/plugin-helper';
import { LivingMapEvents } from './events';

export type LivingMapForceOptions = Omit<MapOptions, 'container'>;

export interface LivingMapOptions {
  zoom: number;
  maxZoom: number;
  minZoom: number;
  center: [number, number];
  extent: [number, number, number, number];
  bearing?: number | 0;
  style: string;
  accessToken: string;
  global?: boolean;
  interactive?: boolean;
  enableTouchPitch?: boolean;
  enablePitchWithRotate?: boolean;
  enableTouchZoomRotate?: boolean;
  hash?: boolean | string;
}

const ZOOM_CHANGE_MAGNITUDE = 1;

class LivingMap extends EventEmitter {
  private filterInstance: FilterKing;
  private styleLoaded = false;
  private fullLoaded = false;
  private config: LivingMapOptions;
  private mapView?: Map | null;
  private element: HTMLElement;
  private plugins: PluginHelper;
  private layerDelegate: LayerDelegate;
  private featureStateDelegate: FeatureStateDelegate;

  public constructor(element: HTMLElement, config: LivingMapOptions) {
    super();
    this.element = element;
    this.config = config;
    this.plugins = new PluginHelper(this);
    this.filterInstance = new FilterKing(this, true);
    this.layerDelegate = new LayerDelegate(this);
    this.featureStateDelegate = new FeatureStateDelegate(this);
  }

  public getConfig(): LivingMapOptions {
    return this.config;
  }

  /**
   * Creates a livingmap object attached to the specified DOM element on the constructor.
   * @returns {LivingMap} LivingMap instance
   */
  public create(): LivingMap {
    const mapboxOptions: MapOptions = {
      center: this.config.center,
      zoom: this.config.zoom,
      container: this.element,
      hash: this.config.hash !== undefined ? this.config.hash : 'hash',
      maxZoom: this.config.maxZoom,
      bearing: this.config.bearing || 0,
      attributionControl: false,
      accessToken: this.config.accessToken,
      interactive:
        this.config.interactive !== undefined ? this.config.interactive : true,
      pitchWithRotate:
        this.config.enablePitchWithRotate !== undefined
          ? this.config.enablePitchWithRotate
          : true,
      touchPitch:
        this.config.enableTouchPitch !== undefined
          ? this.config.enableTouchPitch
          : true,
      touchZoomRotate:
        this.config.enableTouchZoomRotate !== undefined
          ? this.config.enableTouchZoomRotate
          : true,
    };

    if (this.config.global) {
      mapboxOptions.projection = { name: 'globe' };
      mapboxOptions.style = 'mapbox://styles/mapbox/outdoors-v12';
    } else {
      mapboxOptions.style = this.config.style;
      mapboxOptions.minZoom = this.config.minZoom;
      mapboxOptions.maxBounds = [
        [this.config.extent[0], this.config.extent[1]],
        [this.config.extent[2], this.config.extent[3]],
      ];
    }

    this.mapView = new Map(mapboxOptions);

    this.mapView.once('style.load', this.handleStyleLoad);
    this.mapView.once('load', this.handleFullLoad);
    this.mapView.on('dragend', this.onDragEnd);
    this.mapView.on('zoomend', this.onZoomEnd);
    this.mapView.on('moveend', this.onMoveEnd);

    return this;
  }

  /**
   * Destroys the mapping instace.
   * - removes it from the DOM element,
   * - deactives and removes all plugins
   * - clear the mapview from memory.
   * @returns {void}
   */
  public destroy(): void {
    this.plugins.clear();
    this.mapView?.remove();
    this.mapView = null;
    this.fullLoaded = false;
    this.styleLoaded = false;
  }

  /**
   * Returns if the initial map load has been completed.
   * @returns {boolean}
   */
  public hasMapLoaded(): boolean {
    return this.fullLoaded;
  }

  /**
   * Returns if the initial stylesheet load has been completed.
   * @returns {boolean}
   */
  public hasStyledLoaded(): boolean {
    return this.styleLoaded;
  }

  /**
   * Given an initialised Plugin, this method will add & subscribe the method into various mapping data feeds.
   * @param pluginInstance an initialised plugin instance
   * @returns {LivingMap}
   */
  public addPlugin(pluginInstance: LivingMapPlugin): LivingMap {
    this.plugins.add(pluginInstance);
    return this;
  }

  // TODO: requires unit testing
  /**
   * This method will locate any added plugin already available on the map instance.
   * @param id {string}
   * @returns {LivingMapPlugin | undefined} the plugin
   */
  public getPluginById<T extends LivingMapPlugin>(id: string): T {
    return this.plugins.getById(id) as T;
  }

  /**
   * Given a plugin has been added to the map, this method allows it to gracefully be removed.
   * @param pluginInstance an initialised plugin instance
   */
  public removePlugin(pluginInstance: LivingMapPlugin): LivingMap {
    this.plugins.remove(pluginInstance);
    return this;
  }

  /**
   * Disables all available map interactivity. After this has been called the map is essentially a static entetiy.
   * if you require a map to ALWAYS be static, please use those as configuration input options.
   * @returns void
   */
  public disableMapInteractivity(): void {
    if (!this.mapView) return;

    const mapInstance = this.mapView;
    mapInstance.dragPan.disable();
    mapInstance.scrollZoom.disable();
    mapInstance.boxZoom.disable();
    mapInstance.dragRotate.disable();
    mapInstance.keyboard.disable();
    mapInstance.doubleClickZoom.disable();
    mapInstance.doubleClickZoom.disable();
  }

  /**
   * (re-) enables all available map interactivity.
   * @returns void
   */
  public enableMapInteractivity(): void {
    if (!this.mapView) return;

    const mapInstance = this.mapView;
    mapInstance.dragPan.enable();
    mapInstance.scrollZoom.enable();
    mapInstance.boxZoom.enable();
    mapInstance.dragRotate.enable();
    mapInstance.keyboard.enable();
    mapInstance.doubleClickZoom.enable();
    mapInstance.doubleClickZoom.enable();
  }

  /**
   * provides the HTMLelement the map is attached to on the DOM.
   * @returns {HTMLElement} element
   */
  public getElement(): HTMLElement {
    return this.element;
  }

  /**
   * this function will return the underlying mapbox map. Please do note to use this sparingly.
   * we should aim to coply and "livingmap-ify" any calls so that we control it rather than mapbox.
   * @returns {Map}
   */
  public getMapboxMap(): Map {
    if (!this.mapView)
      throw new Error(
        'Map not found, are you sure you have called .create() before accessing this?'
      );
    return this.mapView;
  }

  /**
   * The FilterKing controls all map data filters, and allows you to hide or show (filter) data on the map to change
   * what is shown at a certain location.
   * @returns {FilterKing}
   */
  public getFilterKing(): FilterKing {
    return this.filterInstance;
  }

  /**
   * The LayerDelegate controls and exposes methods to  _programmatically_ added "layers" & data sources related to the map instance.
   * @returns {LayerDelegate}
   */
  public getLayerDelegate(): LayerDelegate {
    return this.layerDelegate;
  }

  /**
   * This Feature state delegate allows you to change the feature-state of 1 individual geometry.
   * With the feature state you are able to conditually style a geometry differently in the same layer.
   * @returns {FeatureStateDelegate}
   */
  public getFeatureStateDelegate(): FeatureStateDelegate {
    return this.featureStateDelegate;
  }

  /**
   * blurs the map. usefull to provide additional attention to a UI modal for example.
   * @returns {void}
   */
  public blur(): void {
    this.element.style.filter = 'blur(5px)';
  }

  /**
   * Opposite of blur(). removes any map blur.
   * @returns {void}
   */
  public unblur(): void {
    this.element.style.filter = '';
  }

  /**
   * Calling this function will prevent the user from rotating the map.
   * Usefull for static maps on webpages or city kiosk with a fixed orientation.
   * @returns {void}
   */
  public setToFixedOrientationMode(): void {
    this.mapView?.dragRotate.disable();
    this.mapView?.touchZoomRotate.disableRotation();
  }

  public getMapCentre(): undefined | LngLat {
    return this.mapView?.getCenter();
  }

  public setMapCentre(center: LngLatLike): void {
    this.mapView?.easeTo({ center });
  }

  public getMapBounds(): LngLatBounds | null {
    if (!this.mapView) return null;
    return this.mapView?.getBounds();
  }

  public getMapZoomLevel(): number | null {
    if (!this.mapView) return null;
    return this.mapView?.getZoom();
  }

  // TODO: requires unit testing
  public easeTo(feature: LMFeature, options?: EasingOptions): void {
    const centroid = feature.getCentroid();
    if (!centroid) return;

    this.mapView?.easeTo({ center: centroid as [number, number], ...options });
  }

  /**
   * NOTE: this can only be called once an initial style has been set.
   * @param  {string | StyleSpecification} newnewStyleUrl either a URL or an actually mapbox style object.
   * @returns void
   */
  public switchStylesheet(newStyle: string | StyleSpecification): void {
    this.mapView?.setStyle(newStyle, {
      diff: true,
      localIdeographFontFamily: undefined,
      localFontFamily: undefined,
    });
    this.mapView?.once('styledata', () => {
      this.handleStyleReload();
    });
  }

  /**
   * Returns an array of GeoJSON Feature objects representing visible features that satisfy the query parameters.
   * @param pointOrBox The geometry of the query region: either a single point or southwest and northeast points describing a bounding box. Omitting this parameter (i.e. calling Map#queryRenderedFeatures with zero arguments, or with only a  options argument) is equivalent to passing a bounding box encompassing the entire map viewport.
   * @param options
   */
  public queryRenderedFeatures(
    pointOrBox?: PointLike | [PointLike, PointLike],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    options?: { layers?: string[]; filter?: any[] } & Parameters<
      Map['setPaintProperty']
    >[3]
  ): LMFeature[] {
    const features = pointOrBox
      ? this.mapView?.queryRenderedFeatures(pointOrBox, options) ?? []
      : this.mapView?.queryRenderedFeatures(options) ?? [];
    return features.map(f => new LMFeature(f));
  }

  /**
   * Returns an array of GeoJSON Feature objects representing features within the specified vector tile or GeoJSON source that satisfy the query parameters.
   * @param sourceID The ID of the vector tile or GeoJSON source to query.
   * @param parameters
   */
  public querySourceFeatures(
    sourceID: string,
    parameters?: {
      sourceLayer?: string;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter?: any[];
    } & Parameters<Map['setPaintProperty']>[3]
  ): LMFeature[] {
    const features =
      this.mapView?.querySourceFeatures(sourceID, parameters) ?? [];
    return features.map(feature => {
      feature.source = sourceID;
      feature.sourceLayer = parameters?.sourceLayer ?? '';
      return new LMFeature(feature);
    });
  }

  public zoomInOneLevel = (): number => {
    const mapInstance = this.getMapboxMap();
    const currentMapZoomLevel = mapInstance.getZoom();
    const newZoom = currentMapZoomLevel - ZOOM_CHANGE_MAGNITUDE;

    mapInstance.easeTo({ zoom: newZoom });
    return newZoom;
  };

  public zoomOutOneLevel = (): number => {
    const mapInstance = this.getMapboxMap();
    const currentMapZoomLevel = mapInstance.getZoom();
    const newZoom = currentMapZoomLevel + ZOOM_CHANGE_MAGNITUDE;

    mapInstance.easeTo({ zoom: newZoom });
    return newZoom;
  };

  private handleFullLoad = () => {
    this.fullLoaded = true;
    this.emit(LivingMapEvents.load);
  };

  private handleStyleLoad = (): void => {
    this.styleLoaded = true;
    this.filterInstance.hydrate();
    this.plugins.activateAll();
    this.emit(LivingMapEvents.styleload);
  };

  private handleStyleReload = (): void => {
    this.getLayerDelegate().reapply();
    this.filterInstance.hydrate();
    this.plugins.refreshAll();

    this.emit(LivingMapEvents.stylereload);
  };

  private onDragEnd = () => {
    this.emit(LivingMapEvents.mapHasFinishedTransitioning);
  };

  private onZoomEnd = () => {
    this.emit(LivingMapEvents.mapHasFinishedTransitioning);
  };

  private onMoveEnd = () => {
    this.emit(LivingMapEvents.mapHasFinishedTransitioning);
  };
}

export default LivingMap;
